/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ActionKernelType,
    ActionList,
    InfoCardKernel,
    InfoCardKernelType,
    UnifiedCollectionInfoCardKernel,
    VodOfferInfoCardKernel,
    WatchFromMyShowsActionKernel,
    WatchFromProviderAction,
    WatchFromSocuActionKernel,
    WatchFromVodActionKernel,
    WatchLiveActionKernel,
    WatchNowAction,
} from 'client-code/actions';
import {
    OfferVodItemKernel,
    UnifiedCollectionVodItemKernel,
    VodItem,
    VodItemType,
} from 'client-code/vod-browse';

import { InfoCardUserAction } from '../../views/common/InfoCard/InfoCardUserAction';
import { DeviceFeatures } from '../device';
import { NdvrInteractor } from '../interactors';
import { Locale, LocaleKeys } from '../locale';

// eslint-disable-next-line no-unused-vars, no-shadow
export enum InfoCardOverlayOptionsType {
    BOOKMARK,
    SEE_MORE_INFO,
    WATCH_FROM_LIVE,
    WATCH_NOW,
    CREATE_ONEPASS,
    MODIFY_ONEPASS,
    RECORD_OPTION,
    WATCH_FROM_MY_SHOW,
    WATCH_FROM_SOCU,
}
export class MetadataOperations {
    // eslint-disable-next-line class-methods-use-this
    public getMetaDataForInfoCardActionsOptions(actions: ActionList) {
        if (actions?.actions?.length === 0) {
            return [];
        }

        // Helper function to create OnePass structure
        const createOnePassStructure = (localeKey: string) => ({
            visible: false,
            text: Locale.getString(localeKey),
            actionType: '',
            isMultiOnePass: false,
            kernel: {
                channelId: '',
                channelNumber: '',
                contentId: '',
                isRecordableOffer: false,
                unifiedCollectionId: '',
            },
            title: '',
            value: {
                actionId: '',
                actionKernelType: '',
            },
        });

        const meta = {
            WATCH_FROM_SOCU: {
                visible: false,
                text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_NOW_FROM_TEXT),
                icons: [] as string[],
                value: {
                    actionType: InfoCardOverlayOptionsType.WATCH_FROM_SOCU,
                    actionKernelType: '',
                    isCatchUp: false,
                    isStartOver: false,
                    socuOfferId: '',
                    channelNumber: '',
                },
            },
            WATCH_NOW: {
                visible: false,
                text: Locale.getString(LocaleKeys.PS_OPTION_WATCH_NOW_DETAIL),
                icons: [] as string[],
                value: {
                    actionType: InfoCardOverlayOptionsType.WATCH_NOW,
                    actionKernelType: '',
                    contentId: '',
                    unifiedCollectionId: '',
                    channelNumber: '',
                    partnerId: '',
                    offerId: '',
                    recordingId: '',
                    collapsedVodItemId: '',
                    contentSupplierPartnerId: '',
                    isRecordingInProgress: false,
                },
            },
            BOOKMARK: {
                visible: false,
                text: '',
                value: {
                    actionType: InfoCardOverlayOptionsType.BOOKMARK,
                    actionKernelType: '',
                    contentId: '',
                    collectionId: '',
                    bookmarkType: '',
                },
            },
            CREATE_ONEPASS: createOnePassStructure(LocaleKeys.RECORD_OVERLAY_OPTION_CREATE_ONEPASS),
            MODIFY_ONEPASS: createOnePassStructure(LocaleKeys.RECORD_OVERLAY_OPTION_MODIFY_ONEPASS),
            RECORD_OPTION: {
                visible: false,
                text: '',
                value: {
                    actionType: InfoCardOverlayOptionsType.RECORD_OPTION,
                    actionKernelType: '',
                    contentId: '',
                    offerId: '',
                },
            },
        };

        // --- Helper functions ---
        const setMetaData = <
            K extends keyof Omit<typeof meta, 'CREATE_ONEPASS' | 'MODIFY_ONEPASS'>,
        >(
            key: K,
            update: Partial<(typeof meta)[K]['value']>,
        ) => {
            meta[key].visible = true;
            meta[key].value = { ...meta[key].value, ...update };
        };

        // --- Categorize Kernel types ---
        const bookmarkKernels: ActionKernelType[] = [
            ActionKernelType.BookmarkThisEpisodeActionKernel,
            ActionKernelType.BookmarkThisEventActionKernel,
            ActionKernelType.BookmarkThisGameActionKernel,
            ActionKernelType.BookmarkThisMatchActionKernel,
            ActionKernelType.BookmarkThisMovieActionKernel,
            ActionKernelType.BookmarkThisRaceActionKernel,
            ActionKernelType.BookmarkThisShowActionKernel,
        ];

        const recordKernels: ActionKernelType[] = [
            ActionKernelType.RecordThisShowActionKernel,
            ActionKernelType.RecordThisEpisodeActionKernel,
            ActionKernelType.RecordThisGameActionKernel,
            ActionKernelType.RecordThisMatchActionKernel,
            ActionKernelType.RecordThisEventActionKernel,
            ActionKernelType.RecordThisRaceActionKernel,
            ActionKernelType.RecordThisMovieActionKernel,
        ];

        // --- Flags ---
        let isVodOffer = false;
        let isVodContent = false;
        let isLive = false;
        let isFromMyShows = false;
        let hasRecording = false;

        // --- Main processing ---
        actions.actions.forEach((action) => {
            const { kernel } = action;
            if (!kernel) return;

            const { actionKernelType } = kernel;

            if (bookmarkKernels.includes(actionKernelType)) {
                const k = kernel as {
                    actionId: string;
                    contentId?: string;
                    collectionId?: string;
                };
                const bookmarkKernelType = k.actionId.split('_').pop() ?? '';
                setMetaData('BOOKMARK', {
                    actionKernelType,
                    contentId: k.contentId ?? '',
                    collectionId: k.collectionId ?? '',
                    bookmarkType: bookmarkKernelType,
                });
                meta.BOOKMARK.text = Locale.getString(
                    LocaleKeys[`PS_OPTION_BOOKMARK_THIS_${bookmarkKernelType}`],
                );
                return;
            }

            if (
                actionKernelType === ActionKernelType.CreateOnePassActionKernel ||
                actionKernelType === ActionKernelType.CreateOnePassActionKernelV2
            ) {
                const k = kernel as {
                    actionId?: string;
                    channelId?: string;
                    channelNumber?: string;
                    contentId?: string;
                    isRecordableOffer?: boolean;
                    unifiedCollectionId?: string;
                    title?: string;
                };
                meta.CREATE_ONEPASS.visible = true;
                meta.CREATE_ONEPASS.actionType = action.actionType ?? '';
                meta.CREATE_ONEPASS.isMultiOnePass =
                    actionKernelType === ActionKernelType.CreateOnePassActionKernelV2;
                meta.CREATE_ONEPASS.kernel = {
                    channelId: k.channelId ?? '',
                    channelNumber: k.channelNumber ?? '',
                    contentId: k.contentId ?? '',
                    isRecordableOffer: k.isRecordableOffer ?? false,
                    unifiedCollectionId: k.unifiedCollectionId ?? '',
                };
                meta.CREATE_ONEPASS.title = k.title ?? '';
                meta.CREATE_ONEPASS.value = {
                    actionId: k.actionId ?? '',
                    actionKernelType,
                };
                return;
            }

            if (
                actionKernelType === ActionKernelType.ModifyOnePassActionKernel ||
                actionKernelType === ActionKernelType.ModifyOnePassActionKernelV2
            ) {
                const k = kernel as {
                    actionId?: string;
                    channelId?: string;
                    channelNumber?: string;
                    contentId?: string;
                    collectionId?: string;
                    isRecordableOffer?: boolean;
                    unifiedCollectionId?: string;
                    title?: string;
                };
                meta.MODIFY_ONEPASS.visible = true;
                meta.MODIFY_ONEPASS.actionType = action.actionType ?? '';
                meta.MODIFY_ONEPASS.isMultiOnePass =
                    actionKernelType === ActionKernelType.ModifyOnePassActionKernelV2;
                meta.MODIFY_ONEPASS.kernel = {
                    channelId: k.channelId ?? '',
                    channelNumber: k.channelNumber ?? '',
                    contentId: k.contentId ?? '',
                    isRecordableOffer: k.isRecordableOffer ?? false,
                    unifiedCollectionId: k.collectionId ?? k.unifiedCollectionId ?? '',
                };
                meta.MODIFY_ONEPASS.title = k.title ?? '';
                meta.MODIFY_ONEPASS.value = {
                    actionId: k.actionId ?? '',
                    actionKernelType,
                };
                return;
            }

            if (recordKernels.includes(actionKernelType)) {
                const k = kernel as { contentId?: string; offerId?: string; actionId: string };
                const recordKernelType = k.actionId.split('_').pop() ?? '';
                setMetaData('RECORD_OPTION', {
                    actionKernelType,
                    contentId: k.contentId ?? '',
                    offerId: k.offerId ?? '',
                });
                meta.RECORD_OPTION.text = Locale.getString(
                    LocaleKeys[`PS_OPTION_RECORD_THIS_${recordKernelType}`],
                );
                return;
            }

            if (actionKernelType === ActionKernelType.WatchFromSocuActionKernel) {
                if (!DeviceFeatures.isSocuEnabled()) return;
                const { provider } = action as WatchFromProviderAction;
                const k = kernel as WatchFromSocuActionKernel;
                setMetaData('WATCH_FROM_SOCU', {
                    actionKernelType,
                    isCatchUp: k.isCatchUp,
                    isStartOver: k.isStartOver,
                    socuOfferId: k.socuOfferId ?? '',
                    channelNumber: k.channelNumber ?? '',
                });
                meta.WATCH_FROM_SOCU.text = `${Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_NOW_FROM_TEXT)} ${
                    provider.displayName
                }`;
                meta.WATCH_FROM_SOCU.icons = [
                    new NdvrInteractor().generateProviderImageUrl(
                        provider.providerId || '',
                        49,
                        49,
                    ),
                ];
                return;
            }

            if (actionKernelType === ActionKernelType.WatchFromMyShowsActionKernel) {
                isLive = (action as any).isLive;
                hasRecording = (action as any).hasRecording;
                isFromMyShows = true;
                return;
            }

            if (actionKernelType === ActionKernelType.WatchLiveActionKernel) {
                isLive = true;
                return;
            }

            if (
                actionKernelType === ActionKernelType.WatchFromVodOfferActionKernel ||
                actionKernelType === ActionKernelType.WatchFromVodActionKernel
            ) {
                if (actionKernelType === ActionKernelType.WatchFromVodOfferActionKernel)
                    isVodOffer = true;
                else isVodContent = true;

                const { providerList } = action as WatchNowAction;
                const services = new NdvrInteractor();
                meta.WATCH_NOW.icons =
                    providerList?.map((provider) =>
                        services.generateProviderImageUrl(provider.providerId || '', 49, 49),
                    ) || [];
            }
        });

        // --- Inject WATCH_NOW metadata based on flags (Order followed as per specs) ---
        if (isLive && isFromMyShows) {
            const myShowsActionKernel = actions.actions.find(
                (action) =>
                    action.kernel.actionKernelType ===
                    ActionKernelType.WatchFromMyShowsActionKernel,
            )?.kernel as WatchFromMyShowsActionKernel;
            meta.WATCH_NOW.icons = myShowsActionKernel.isRecordingInProgress
                ? [
                      'diskIcon',
                      'liveIcon',
                      ...meta.WATCH_NOW.icons,
                      ...this.addProviderIcon(
                          actions,
                          ActionKernelType.WatchFromMyShowsActionKernel,
                      ),
                  ]
                : [
                      'liveIcon',
                      ...meta.WATCH_NOW.icons,
                      ...this.addProviderIcon(
                          actions,
                          ActionKernelType.WatchFromMyShowsActionKernel,
                      ),
                  ];
            setMetaData('WATCH_NOW', {
                actionKernelType: ActionKernelType.WatchFromMyShowsActionKernel,
                recordingId: myShowsActionKernel.recordingId,
                isRecordingInProgress: myShowsActionKernel.isRecordingInProgress,
            });
        } else if (isLive) {
            const liveActionKernel = actions.actions.find(
                (action) =>
                    action.kernel.actionKernelType === ActionKernelType.WatchLiveActionKernel,
            )?.kernel as WatchLiveActionKernel;
            meta.WATCH_NOW.icons = [
                'liveIcon',
                ...meta.WATCH_NOW.icons,
                ...this.addProviderIcon(actions, ActionKernelType.WatchLiveActionKernel),
            ];
            setMetaData('WATCH_NOW', {
                actionKernelType: ActionKernelType.WatchLiveActionKernel,
                channelNumber: liveActionKernel.channelNumber ?? '',
                contentId: liveActionKernel.actionId ?? '',
            });
        } else if (isFromMyShows) {
            const myShowsActionKernel = actions.actions.find(
                (action) =>
                    action.kernel.actionKernelType ===
                    ActionKernelType.WatchFromMyShowsActionKernel,
            )?.kernel as WatchFromMyShowsActionKernel;
            meta.WATCH_NOW.icons = [
                ...(hasRecording ? ['diskIcon'] : []),
                ...this.addProviderIcon(actions, ActionKernelType.WatchFromMyShowsActionKernel),
            ];
            setMetaData('WATCH_NOW', {
                actionKernelType: ActionKernelType.WatchFromMyShowsActionKernel,
                recordingId: myShowsActionKernel.recordingId,
                isRecordingInProgress: myShowsActionKernel.isRecordingInProgress,
            });
        } else if (isVodOffer) {
            const vodActionKernel = actions.actions.find(
                (action) =>
                    action.kernel.actionKernelType ===
                    ActionKernelType.WatchFromVodOfferActionKernel,
            )?.kernel as any;
            setMetaData('WATCH_NOW', {
                actionKernelType: ActionKernelType.WatchFromVodOfferActionKernel,
                offerId: vodActionKernel.offerId,
                partnerId: vodActionKernel?.provider?.providerId,
            });
        } else if (isVodContent) {
            const vodActionKernel = actions.actions.find(
                (action) =>
                    action.kernel.actionKernelType === ActionKernelType.WatchFromVodActionKernel,
            )?.kernel as WatchFromVodActionKernel;
            setMetaData('WATCH_NOW', {
                actionKernelType: ActionKernelType.WatchFromVodActionKernel,
                contentId: vodActionKernel.contentId,
                partnerId: vodActionKernel.partnerId,
                collapsedVodItemId: vodActionKernel.collapsedVodItemId,
                contentSupplierPartnerId: vodActionKernel.contentSupplierPartnerId,
            });
        }

        // --- Build final list ---
        const list: InfoCardUserAction[] = [];
        const pushIfVisible = <K extends keyof typeof meta>(key: K) => {
            const item = meta[key];
            if (!item.visible) return;

            if (key === 'CREATE_ONEPASS' || key === 'MODIFY_ONEPASS') {
                const onePassItem = item as
                    | (typeof meta)['CREATE_ONEPASS']
                    | (typeof meta)['MODIFY_ONEPASS'];
                list.push({
                    id: onePassItem.text,
                    visible: true,
                    text: onePassItem.text,
                    actionType: onePassItem.actionType,
                    isMultiOnePass: onePassItem.isMultiOnePass,
                    kernel: { ...onePassItem.kernel },
                    title: onePassItem.title,
                    value: { ...onePassItem.value },
                });
                return;
            }

            const baseAction: InfoCardUserAction = {
                id: key,
                visible: true,
                text: item.text,
                value: { ...item.value },
            };
            if ('icons' in item) {
                baseAction.icons = item.icons;
            }

            list.push(baseAction);
        };

        (Object.keys(meta) as Array<keyof typeof meta>).forEach(pushIfVisible);

        return list;
    }

    private addProviderIcon(actions: ActionList, actionKernelType: ActionKernelType): string[] {
        const liveAction = actions.actions.find(
            (action) => action.kernel.actionKernelType === actionKernelType,
        ) as WatchNowAction | undefined;
        const providerList = liveAction?.providerList || [];
        const services = new NdvrInteractor();
        return (
            providerList?.map((provider) =>
                services.generateProviderImageUrl(provider.providerId || '', 49, 49),
            ) || []
        );
    }
}
